/**
 * "… with Letterhead / … without Letterhead" chooser for the procurement PDFs.
 *
 * Mirrors the menu the OPD billing screens already use (billing-payment/ViewInvoice,
 * RefundReceipt, appointments/physiotherapy), so the inventory PDFs behave the way
 * users already expect elsewhere in the app.
 *
 * One menu instance serves every PDF button on a screen: the button that was clicked
 * becomes the anchor and supplies the callback, which is then invoked with the chosen
 * mode. Hospitals printing on pre-printed stationery pick "without" so the facility
 * header and Hlthtek footer are left off — the layout is identical either way, so the
 * content still lines up with the paper.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';

/**
 * @returns {{anchorEl: HTMLElement|null,
 *            open: (event: React.MouseEvent, onPick: (withLetterhead: boolean) => void) => void,
 *            close: () => void,
 *            pick: (withLetterhead: boolean) => void}}
 */
export function useLetterheadMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [handler, setHandler] = useState(null);

  const open = useCallback((event, onPick) => {
    setAnchorEl(event.currentTarget);
    // Wrapped in a setter callback — React would otherwise call a bare function
    // argument as a state updater instead of storing it.
    setHandler(() => onPick);
  }, []);

  const close = useCallback(() => setAnchorEl(null), []);

  const pick = useCallback((withLetterhead) => {
    setAnchorEl(null);
    if (handler) handler(withLetterhead);
  }, [handler]);

  // Memoised: list screens put this in the dependency array of the useMemo that
  // builds their DataGrid columns, and a fresh object every render would rebuild
  // every column on every render.
  return useMemo(() => ({ anchorEl, open, close, pick }), [anchorEl, open, close, pick]);
}

export default function LetterheadMenu({ menu, label }) {
  return (
    <Menu
      anchorEl={menu.anchorEl}
      open={Boolean(menu.anchorEl)}
      onClose={menu.close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: { minWidth: 260 } }}
    >
      <MenuItem onClick={() => menu.pick(true)}>{label} with Letterhead</MenuItem>
      <MenuItem onClick={() => menu.pick(false)}>{label} without Letterhead</MenuItem>
    </Menu>
  );
}
